import type { LearnerClient, LearnerView, PlanView } from "@tutor/live";
import type { Mood } from "@tutor/protocol";
import { useEffect, useId, useRef, useState } from "react";
import { useLocale, useT, type Translate } from "../i18n";
import { skillLabel } from "../insights/skill-labels";
import { SkillSteps } from "./Progress";

// « Fin » (feature 7): the session's last screen. Today's progression, the plan's ticks
// and the next step are the engine's; the goodbye is fixed copy picked from them; the mood
// is one tap with no free text. Nothing here is generated.

const MOODS: readonly Mood[] = ["good", "meh", "hard"];

/** Skills graded this session, the plan's own first, each with its step before it. */
export function todaysClimbs(view: LearnerView): { skillId: string; from: number }[] {
  const planned = view.plan?.goals.map((g) => g.skillId) ?? [];
  const graded = Object.keys(view.climbs);
  const order = [
    ...planned.filter((s) => graded.includes(s)),
    ...graded.filter((s) => !planned.includes(s)),
  ];
  return order.map((skillId) => ({ skillId, from: view.climbs[skillId] ?? 0 }));
}

function goodbye(t: Translate, view: LearnerView, plan: PlanView): string {
  const climbed = Object.entries(view.climbs).some(
    ([skillId, from]) => (view.progress[skillId]?.step ?? 0) > from,
  );
  if (climbed) return t("fin.bye.step");
  if (plan.goals.length > 0 && plan.goals.every((g) => g.state === "done"))
    return t("fin.bye.plan");
  return t("fin.bye.other");
}

export function Fin(props: {
  view: LearnerView;
  plan: PlanView;
  client: LearnerClient;
  /** The child changed their mind before closing. */
  onBack: () => void;
}) {
  const { view, plan, client, onBack } = props;
  const t = useT();
  const locale = useLocale();
  const titleId = useId();
  const moodId = useId();
  // Opened from the bottom of the board: bring the child (and a screen reader) to the top.
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const ended = view.ended?.sessionStart === plan.sessionStart ? view.ended : null;
  const [mood, setMood] = useState<Mood | null>(null);
  const shownMood = ended === null ? mood : ended.mood;
  const climbs = todaysClimbs(view);
  const finished = view.pages.length + (view.item?.done === true ? 1 : 0);
  const firstTry =
    view.pages.filter((p) => p.attempts.length === 1 && p.attempts[0]?.server === true).length +
    (view.item?.done === true &&
    view.item.attempts.length === 1 &&
    view.item.attempts[0]?.server === true
      ? 1
      : 0);
  const next = plan.goals.find((g) => g.state !== "done");
  return (
    <section className="fin" aria-labelledby={titleId}>
      <p className="fin-kicker">{t("fin.kicker")}</p>
      <h2 id={titleId} ref={titleRef} tabIndex={-1} className="fin-title">
        {t("fin.title")}
      </h2>
      <div className="fin-grid">
        <div className="fin-climbs">
          {climbs.map(({ skillId, from }) => {
            const progress = view.progress[skillId];
            const fresh = progress !== undefined && progress.step > from;
            return (
              <article key={skillId} className="item-card fin-skill">
                <h3 className="fin-skill-title">
                  {skillLabel(skillId, locale)}
                  {fresh && <span className="fin-badge">{t("fin.newStep")}</span>}
                </h3>
                <SkillSteps skillId={skillId} progress={progress} fresh={fresh} />
              </article>
            );
          })}
          <p className="fin-count">{t("fin.count", { n: finished, m: firstTry })}</p>
        </div>
        <div className="fin-side">
          {plan.goals.length > 0 && (
            <div className="fin-card">
              <h3 className="fin-card-title">{t("fin.plan")}</h3>
              <ul className="fin-plan">
                {plan.goals.map((goal) => {
                  const skill = skillLabel(goal.skillId, locale);
                  const done = goal.state === "done";
                  return (
                    <li key={goal.skillId} data-done={done}>
                      <span className="fin-tick" aria-hidden="true">
                        {done ? "✓" : ""}
                      </span>
                      {done
                        ? t(goal.kind === "review" ? "board.goal.review" : "board.goal.climb", {
                            skill,
                          })
                        : t("fin.kept", { skill })}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="fin-card">
            {next !== undefined && (
              <p className="fin-next">
                {t("fin.next")}{" "}
                <b>
                  {t(next.kind === "review" ? "board.goal.review" : "board.goal.climb", {
                    skill: skillLabel(next.skillId, locale),
                  })}
                </b>
              </p>
            )}
            <p className="fin-bye">
              <span className="fin-coach" aria-hidden="true">
                C
              </span>
              <span>{goodbye(t, view, plan)}</span>
            </p>
          </div>
          <div className="fin-card">
            <h3 id={moodId} className="fin-card-title">
              {t("fin.mood")}
            </h3>
            <div className="fin-moods" role="radiogroup" aria-labelledby={moodId}>
              {MOODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  className="fin-mood"
                  aria-checked={shownMood === m}
                  disabled={ended !== null}
                  onClick={() => {
                    setMood(shownMood === m ? null : m);
                  }}
                >
                  <Face mood={m} />
                  {t(`fin.mood.${m}`)}
                </button>
              ))}
            </div>
            {ended === null && <p className="fin-hint">{t("fin.moodHint")}</p>}
          </div>
          {ended === null ? (
            <div className="fin-actions">
              <button type="button" className="button ghost" onClick={onBack}>
                {t("fin.back")}
              </button>
              <button
                type="button"
                className="button primary big"
                onClick={() => {
                  client.endSession(plan.sessionStart, mood);
                }}
              >
                {t("fin.close")}
              </button>
            </div>
          ) : (
            <div className="fin-actions">
              <p className="fin-closed" role="status">
                {t("fin.closed")}
              </p>
              <button
                type="button"
                className="button ghost"
                onClick={() => {
                  client.requestItem(locale);
                }}
              >
                {t("fin.again")}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** A line face: a smile, a flat mouth or a frown. */
function Face(props: { mood: Mood }) {
  const mouth =
    props.mood === "good"
      ? "M8 14q4 3.5 8 0"
      : props.mood === "meh"
        ? "M8 15h8"
        : "M8 16q4 -3.5 8 0";
  return (
    <svg className="fin-face" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="9" cy="10" r="1" className="fin-eye" />
      <circle cx="15" cy="10" r="1" className="fin-eye" />
      <path d={mouth} />
    </svg>
  );
}
